import { SubmissionErrors } from 'final-form';
import React, { ReactNode } from 'react';
import { Field, Form } from 'react-final-form';
import { connect } from 'react-redux';
import { withTranslation, WithTranslation } from 'react-i18next';
import { Col } from 'antd';

import { extractCoreValues, getUserFullName, UserSettingsAttrs } from '~src/interfaces/user.interface';
import SecondaryButton from '~src/shared/buttons/basicButtons/SecondaryButton.sc';
import SelectInput from '~src/shared/form/select/SelectInput';
import ImageDropZone from '~src/shared/imageUploader/ImageDropZone';
import { composeValidators } from '~src/shared/reactFinalForm/validators';
import { TileContainer } from '~src/styles/containers.sc';
import {
  SubSection,
  SectionHeader,
  SectionBody,
  Footer,
  ActionRow,
  ButtonSection,
} from '~src/shared/form/shared.sc';
import {
  validatorRequired,
  validatorEmail,
  validatorMaxLength80,
  validatorEmptyOr,
} from '~src/services/validators';
import PrimaryButtonLoading from '~src/shared/buttons/loadingButtons/PrimaryButtonLoading';
import CustomFormInput from '~src/shared/form/inputs/FormInput';
import TileHeader from '~src/shared/headers/tileHeader/TileHeader';
import { SegmentType } from '~src/interfaces/segment.interface';
import { StoreI } from '~src/rootReducer';
import { TimezonesI, SessionUserI, SystemLocalesI, ContentLocalesI } from '~src/interfaces/session.types';
import {
  getTimezoneAttribute,
  getSystemLanguageAttribute,
  getContentLanguageAttribute,
  getManagerAttribute,
  getRecommendationsAttribute,
} from '~src/services/attributes';
import { AttributeI } from '~src/interfaces/attributes.types';
import { FieldType } from '~src/types';

import {
  ManagerContainer,
  ManagerContainerContent,
  ManagerContainerHeader,
  UserTemplateError,
  ManagerPlaceholder,
} from './userSettingsForm.sc';

export interface FormValuesI {
  deactivated: boolean;
  email: string;
  firstname: string;
  lastname: string;
  username?: string;
  attributes: UserSettingsAttrs;
}

interface StateProps {
  sessionUser: SessionUserI;
  timezones: TimezonesI;
  timezoneAttribute: AttributeI;
  systemLanguageAttribute: AttributeI;
  contentLanguageAttribute: AttributeI;
  recommendationsAttribute: AttributeI;
  managerAttribute: AttributeI;
  initialValues: FormValuesI;
}

interface OwnProps {
  systemLocales: SystemLocalesI;
  contentLocales: ContentLocalesI;
  onFrmSubmit(
    values: FormValuesI,
    sessionUser: SessionUserI,
    initialValues: Partial<FormValuesI>,
  ): Promise<SubmissionErrors | void>;
}

type Props = OwnProps & StateProps & WithTranslation;

class UserSettingsForm extends React.Component<Props> {
  mapAttributeValues = (values: FormValuesI) => {
    const { sessionUser, initialValues } = this.props;
    return this.props.onFrmSubmit(values, sessionUser, initialValues);
  };

  checkForError(pristine: boolean, anyTouched: boolean, submitFailed: boolean, valid: boolean) {
    if ((pristine && anyTouched) || (!pristine && submitFailed)) {
      return !valid;
    }
    return false;
  }

  renderField = ({ autoFocus, input, label, meta: { touched, error }, labelKey }: FieldType): ReactNode => (
    <CustomFormInput
      {...input}
      autoFocus={autoFocus}
      label={label}
      type={input.type || 'text'}
      error={touched ? error : undefined}
      labelKey={labelKey}
    />
  );

  renderManagerField({ input, label, placeholder, placeholderKey }: FieldType): ReactNode {
    return (
      <ManagerContainer>
        <ManagerContainerHeader>{label}</ManagerContainerHeader>
        <ManagerContainerContent>
          {input.value || (
            <ManagerPlaceholder data-translation={placeholderKey}>{placeholder}</ManagerPlaceholder>
          )}
        </ManagerContainerContent>
      </ManagerContainer>
    );
  }

  renderSelectInput = ({
    input,
    list,
    label,
    translationKey,
    meta: { touched, error },
  }: FieldType & { list: TimezonesI }): ReactNode => {
    const optList = Object.keys(list).map(k => ({ name: list[k], value: k }));

    return (
      <SelectInput
        {...input}
        t={this.props.t}
        label={label}
        optList={optList}
        error={touched ? error : null}
        defaultValue={input.value}
        labelKey={translationKey}
      />
    );
  };

  render() {
    const { t, sessionUser, initialValues } = this.props;
    return (
      <TileContainer>
        <Form onSubmit={this.mapAttributeValues} initialValues={initialValues} keepDirtyOnReinitialize>
          {({ touched, submitting, submitFailed, valid, handleSubmit, pristine, form, values }) => {
            const formHeader = getUserFullName(values);
            const reset = () => form.reset();

            return (
              <form onSubmit={handleSubmit} noValidate>
                <TileHeader
                  title={formHeader}
                  avatar={{ type: SegmentType.User, url: sessionUser.imageUrl, name: formHeader }}
                  border
                />
                <SectionHeader data-translation="x.profile_settings.section.basic">
                  {t('x.profile_settings.section.basic')}
                </SectionHeader>
                <SectionBody>
                  <SubSection>
                    <Col lg={8}>
                      <Field
                        name="email"
                        label={t('x.profile_settings.email')}
                        type="text"
                        children={this.renderField}
                        validate={validatorEmptyOr(composeValidators([validatorEmail, validatorMaxLength80]))}
                        labelKey="x.profile_settings.email"
                      />
                    </Col>
                    <Col lg={8}>
                      <Field
                        name="username"
                        label={t('x.profile_settings.username')}
                        type="text"
                        children={this.renderField}
                        validate={validatorMaxLength80}
                        labelKey="x.profile_settings.username"
                      />
                    </Col>
                    <Col lg={8}>
                      <Field
                        name={`attributes.${this.props.managerAttribute.uuid}`}
                        label={t('x.profile_settings.manager')}
                        type="text"
                        children={this.renderManagerField}
                        translationKey="x.profile_settings.manager"
                        placeholder={t('x.profile_settings.manager.placeholder')}
                        placeholderKey="x.profile_settings.manager.placeholder"
                      />
                    </Col>
                  </SubSection>
                  <SubSection>
                    <Col lg={8}>
                      <Field
                        autoFocus
                        name="firstname"
                        label={t('x.profile_settings.firstname')}
                        type="text"
                        children={this.renderField}
                        validate={validatorRequired}
                        labelKey="x.profile_settings.firstname"
                      />
                    </Col>
                    <Col lg={8}>
                      <Field
                        name="lastname"
                        label={t('x.profile_settings.lastname')}
                        type="text"
                        children={this.renderField}
                        validate={validatorRequired}
                        labelKey="x.name"
                      />
                    </Col>
                    <Col lg={8}>
                      <ImageDropZone userId={sessionUser.uuid} type="user" isInForm />
                    </Col>
                  </SubSection>
                </SectionBody>
                <SectionHeader data-translation="x.profile_settings.section.advanced">
                  {t('x.profile_settings.section.advanced')}
                </SectionHeader>
                <SectionBody>
                  <SubSection>
                    <Col lg={8}>
                      <Field
                        name={`attributes.${this.props.timezoneAttribute.uuid}`}
                        label={t('x.company.timezones')}
                        children={this.renderSelectInput}
                        list={this.props.timezones}
                        validate={validatorRequired}
                        translationKey="x.company.timezones"
                      />
                    </Col>
                    <Col lg={8}>
                      <Field
                        name={`attributes.${this.props.systemLanguageAttribute.uuid}`}
                        label={t('x.company.prefered_system_language')}
                        children={this.renderSelectInput}
                        list={this.props.systemLocales}
                        validate={validatorRequired}
                        translationKey="x.company.prefered_system_language"
                      />
                    </Col>
                    <Col lg={8}>
                      <Field
                        name={`attributes.${this.props.contentLanguageAttribute.uuid}`}
                        label={t('x.company.prefered_content_language')}
                        children={this.renderSelectInput}
                        list={this.props.contentLocales}
                        validate={validatorRequired}
                        translationKey="x.company.prefered_content_language"
                      />
                    </Col>
                  </SubSection>
                </SectionBody>
                {!pristine && (
                  <Footer>
                    <ActionRow>
                      <ButtonSection>
                        <SecondaryButton onClick={reset} type="reset">
                          <span data-translation="x.button.cancel">{t('x.button.cancel')}</span>
                        </SecondaryButton>
                        <PrimaryButtonLoading isLoading={submitting}>
                          <span data-translation="x.button.save">{t('x.button.save')}</span>
                        </PrimaryButtonLoading>
                      </ButtonSection>
                    </ActionRow>
                    {this.checkForError(
                      pristine,
                      Object.values(touched || {}).length > 0,
                      submitFailed,
                      valid,
                    ) && (
                      <UserTemplateError data-translation="x.surveys.schedules.template_error">
                        {t('x.surveys.schedules.template_error')}
                      </UserTemplateError>
                    )}
                  </Footer>
                )}
              </form>
            );
          }}
        </Form>
      </TileContainer>
    );
  }
}

function getManager(sessionUser: SessionUserI, managerAttrId: string): string | null {
  const manager = sessionUser.manager_attributes[managerAttrId];
  return manager ? getUserFullName(manager) : null;
}

function getAttributeValuesFromState(
  sessionUser: SessionUserI,
  managerAttribute: AttributeI,
  timezoneAttribute: AttributeI,
  systemLanguageAttribute: AttributeI,
  contentLanguageAttribute: AttributeI,
  recommendationsAttribute: AttributeI,
): UserSettingsAttrs {
  const { setting_attributes } = sessionUser;
  return {
    [timezoneAttribute.uuid]: setting_attributes[timezoneAttribute.uuid],
    [systemLanguageAttribute.uuid]: setting_attributes[systemLanguageAttribute.uuid],
    [contentLanguageAttribute.uuid]: setting_attributes[contentLanguageAttribute.uuid],
    [recommendationsAttribute.uuid]: setting_attributes[recommendationsAttribute.uuid],
    [managerAttribute.uuid]: getManager(sessionUser, managerAttribute.uuid),
  };
}

const mapState2Props = (state: StoreI): StateProps => {
  const managerAttribute = getManagerAttribute(state);
  const timezoneAttribute = getTimezoneAttribute(state);
  const systemLanguageAttribute = getSystemLanguageAttribute(state);
  const contentLanguageAttribute = getContentLanguageAttribute(state);
  const recommendationsAttribute = getRecommendationsAttribute(state);
  return {
    sessionUser: state.user.session.user!,
    timezoneAttribute,
    timezones: state.user.session.timezones,
    systemLanguageAttribute,
    contentLanguageAttribute,
    managerAttribute,
    recommendationsAttribute,
    initialValues: {
      ...extractCoreValues(state.user.session.user!),
      attributes: getAttributeValuesFromState(
        state.user.session.user!,
        managerAttribute,
        timezoneAttribute,
        systemLanguageAttribute,
        contentLanguageAttribute,
        recommendationsAttribute,
      ),
    },
  };
};

const UserSettingsFormT = withTranslation()(UserSettingsForm);
const UserSettingsFormC = connect(mapState2Props)(UserSettingsFormT);

export default UserSettingsFormC;
